import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useVehicle } from "../../../lib/vehicleContext";
import { warrantyFor } from "../../../lib/brand";
import { getClimate } from "../../../lib/climateData";
import {
  computeEviqScore,
  forecastDegradation,
  detectAnomalies,
} from "../../../lib/intelligenceScore";
import { TeslaSignInCard } from "../TeslaSignInCard";
import { GoogleReviewCard } from "../GoogleReviewCard";
import { HomeSkeleton } from "./HomeSkeleton";
import { ConnectionProgress } from "./ConnectionProgress";
import { ChargingTodayCard } from "../ChargingTodayCard";
import { PhantomDrainCard } from "../PhantomDrainCard";
import { FordWallboxCard } from "../FordWallboxCard";
import { ModificationsCard } from "../ModificationsCard";
import { EarthImpactCard } from "../EarthImpactCard";
import { ShareAppCard } from "../ShareAppCard";
import { VehiclePhotoCard } from "../VehiclePhotoCard";
import { EviqScoreCard } from "../EviqScoreCard";
import { VehicleBaselineStrip } from "../VehicleBaselineStrip";
import { batteryRiskScore } from "../../../lib/batteryAnalytics";
import { Shield, Dna, ArrowRight } from "lucide-react";
import { CollapseSection } from "../CollapseSection";
import {
  VEHICLE_USAGE_HINTS,
  HealthChart,
  StatCard,
  SubScoreRadarChart,
  PredictorSlot,
  LiveChip,
} from "./_shared";

export const HomeView = ({ stateCode }) => {
  const { t } = useTranslation();
  const { vehicle, live } = useVehicle();
  const climate = useMemo(() => getClimate(stateCode), [stateCode]);
  const hints = VEHICLE_USAGE_HINTS[vehicle.id] || VEHICLE_USAGE_HINTS["model-3-lr"];

  const anomalies = useMemo(
    () =>
      detectAnomalies({
        monthlyLoss: Math.abs(vehicle.delta30d || 0.2),
        fleetAvgLoss: 0.18,
        superchargerPct: hints.supercharger_pct,
        parkedAt100PctDaysPerMonth: hints.parked_at_100pct_days_per_month,
        climateZone: climate.zone,
        phantomDrainPct: 1.2,
        cellImbalanceMv: 14,
      }),
    [vehicle, hints, climate]
  );

  const score = useMemo(
    () =>
      computeEviqScore({
        soh: vehicle.sohCurrent,
        delta30d: vehicle.delta30d || -0.2,
        climateZone: climate.zone,
        superchargerPct: hints.supercharger_pct,
        parkedAt100PctDaysPerMonth: hints.parked_at_100pct_days_per_month,
        preconditionsInWinter: hints.preconditions_in_winter,
        anomalies,
      }),
    [vehicle, climate, hints, anomalies]
  );

  const forecast = useMemo(
    () =>
      forecastDegradation({
        currentSoh: vehicle.sohCurrent,
        monthlyLoss: Math.abs(vehicle.delta30d || 0.2),
        climateZone: climate.zone,
        warrantyThreshold: vehicle.warranty?.threshold || 70,
      }),
    [vehicle, climate]
  );

  const gradeColor =
    score.grade.startsWith("A")
      ? "#39FF14"
      : score.grade.startsWith("B")
      ? "#00E5FF"
      : score.grade === "C"
      ? "#FFB020"
      : "#FF6B57";

  const willCross = forecast.yearsToWarranty !== null && forecast.yearsToWarranty < 8;

  // When Tesla is connected but the Fleet API returned an empty vehicles
  // list, surface a clear diagnostic instead of silently falling back to
  // demo data. Common cause: the user needs to re-pair the app in the
  // Tesla mobile app's "Third-Party Apps" section.
  const teslaConnectedButNoCars =
    live?.connected && live?.vehiclesLoaded && (live?.vehicles?.length ?? 0) === 0;

  // Show the shimmering skeleton whenever real Tesla data hasn't merged.
  // This covers three states — no session, connecting, and no-vehicles —
  // so we never leak the mock catalog into the app.
  const showSkeleton = !vehicle?.live;

  return (
    <div className="pt-4 space-y-4" data-testid="mobile-app-view-home">
      <TeslaSignInCard />

      <VehiclePhotoCard vehicle={vehicle} />

      {live?.connected && !vehicle?.live && !teslaConnectedButNoCars && (
        <ConnectionProgress live={live} />
      )}

      {teslaConnectedButNoCars && (
        <div
          data-testid="tesla-no-vehicles-warning"
          className="rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/[0.06] p-3 text-[12px] text-white leading-relaxed"
        >
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#FFB020] mb-1">
            {live.vehiclesReason === "missing_scope"
              ? "Tesla connected · vehicle access not granted"
              : "Tesla connected · no vehicles returned"}
          </div>
          {live.vehiclesReason === "missing_scope" ? (
            <>
              Your Tesla sign-in went through, but the <b>Vehicle Information</b>{" "}
              permission wasn't granted — Tesla returns an empty vehicle list
              without it.
              <br />
              Tap <b>Reconnect</b> below and keep <b>all permission boxes
              checked</b> on Tesla's consent screen.
            </>
          ) : (
            <>
              Your Tesla session is authenticated, but the Fleet API returned an
              empty vehicle list — showing a placeholder below.
              <br />
              Tap <b>Reconnect</b> below and make sure all permission boxes stay
              checked. If it persists, open the <b>Tesla app</b> →{" "}
              <b>Settings</b> → <b>Third-Party Apps</b> and confirm <b>EVIQ</b>{" "}
              has access to your vehicles.
            </>
          )}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/tesla/authorize?return_to=/app`}
            className="mt-2.5 flex items-center justify-center gap-2 w-full py-2 rounded-lg font-mono text-[11px] uppercase tracking-wider font-bold border transition-colors"
            style={{ color: "#00E5FF", borderColor: "rgba(0,229,255,0.4)", background: "rgba(0,229,255,0.08)" }}
            data-testid="tesla-reconnect-btn"
          >
            Reconnect with full access
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
          </a>
          {live.vehiclesError && (
            <div className="mt-1 font-mono text-[10px] text-zinc-400 opacity-70">
              {live.vehiclesError}
            </div>
          )}
        </div>
      )}

      {showSkeleton && (
        <>
          <HomeSkeleton />
          {/* Even without live Tesla data we want the Earth Impact card
              visible — it's the Earth Initiative's beachhead in the app
              and works fine off the demo VIN (falls into the empty-state). */}
          <EarthImpactCard vin={vehicle?.vin} />
        </>
      )}

      {!showSkeleton && (
        <>
          <div className="text-center relative">
        {vehicle.live && (
          <div className="absolute top-0 right-0" data-testid="score-live-wrap">
            <LiveChip testid="score-live" />
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          {t("app.score_title", "Predictive insights, not just numbers")}
        </div>
        <div className="relative inline-block mt-3">
          <svg width="200" height="200" className="-rotate-[225deg]">
            <defs>
              <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#00E5FF" />
                <stop offset="100%" stopColor="#39FF14" />
              </linearGradient>
            </defs>
            <circle cx="100" cy="100" r="86" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="14"
              strokeDasharray={`${2 * Math.PI * 86 * 0.75} ${2 * Math.PI * 86}`} strokeLinecap="round" />
            <circle cx="100" cy="100" r="86" fill="none" stroke="url(#scoreGrad)" strokeWidth="14"
              strokeDasharray={`${(score.score / 100) * (2 * Math.PI * 86 * 0.75)} ${2 * Math.PI * 86}`}
              strokeLinecap="round" style={{ filter: "drop-shadow(0 0 10px #00E5FFaa)" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-display text-5xl text-white font-semibold" data-testid="home-score-value">
              {Math.round(score.score)}
            </div>
            <div className="font-mono text-[11px] text-zinc-500 mt-1">/ 100</div>
            <div
              className="mt-2 font-mono text-[10px] px-2 py-0.5 rounded-full border"
              style={{
                borderColor: `${gradeColor}66`,
                color: gradeColor,
                background: `${gradeColor}15`,
              }}
            >
              {t("app.grade", "Grade")} {score.grade}
            </div>
          </div>
        </div>
        {/* Sub-title — only include climate/state when we have real GPS
            from Tesla. Otherwise showing "California" (the default
            state code) misleads users in other states whose cars are
            asleep and haven't reported location yet. */}
        <div className="text-xs text-zinc-400 mt-3">
          {vehicle.year} {vehicle.short}
          {vehicle.live && vehicle.location?.lat != null && vehicle.location?.lng != null && (
            <> · {climate.label}</>
          )}
          {vehicle.live && vehicle.location?.lat == null && (
            <span className="text-zinc-500"> · Waiting for location…</span>
          )}
        </div>
      </div>

      <HealthChart vehicle={vehicle} testid="mobile-app-home-chart" />

      <VehicleBaselineStrip vehicle={vehicle} />

      {/* Battery Risk Score — headline "should I worry" answer */}
      {vehicle?.live && (() => {
        const risk = batteryRiskScore(vehicle, climate);
        const c = risk.band === "Low" ? "#39FF14" : risk.band === "Moderate" ? "#FFB020" : "#FF6B57";
        return (
          <div
            className="rounded-2xl border p-4"
            style={{ borderColor: `${c}44`, background: `${c}0d` }}
            data-testid="home-battery-risk"
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center border"
                style={{ background: `${c}18`, borderColor: `${c}66` }}
              >
                <Shield className="w-4 h-4" style={{ color: c }} strokeWidth={2.4} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
                  Battery risk score
                </div>
                <div className="text-sm text-white font-semibold leading-snug mt-0.5">
                  {risk.band} risk today
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-2xl font-semibold leading-none" style={{ color: c }}>
                  {risk.value}%
                </div>
                <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
                  {risk.band === "Low" ? "no concerns" : risk.band === "Moderate" ? "watch trend" : "book service"}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <EviqScoreCard vehicle={vehicle} climate={climate} />

      <div className="grid grid-cols-2 gap-3">
        <StatCard label="EVIQ Estimated SoH" value={`${vehicle.sohCurrent}%`} trend={`fleet ${vehicle.fleetAvg}%`} color="#39FF14" live={vehicle.live} />
        <StatCard label="Degradation" value={`${Math.abs(vehicle.delta30d).toFixed(1)}%`} trend="last 30 days" color="#00E5FF" live={vehicle.live} />
        <StatCard label="Warranty room" value={`${(vehicle.sohCurrent - warrantyFor(vehicle).threshold).toFixed(1)}%`} trend="above floor" color="#39FF14" live={vehicle.live} />
        <StatCard
          label="Miles headroom"
          value={(warrantyFor(vehicle).miles - vehicle.odometer).toLocaleString()}
          trend={`of ${(warrantyFor(vehicle).miles / 1000).toFixed(0)}k`}
          color="#FFB020"
          live={vehicle.live}
        />
      </div>

      <SubScoreRadarChart
        factors={score.factors}
        testid="mobile-app-subscore-chart"
        isLive={vehicle.live}
      />

      <CollapseSection id="home-outlook" eyebrow="Forecast" title="Warranty outlook">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 relative">
        {vehicle.live && (
          <div className="absolute top-2 right-2">
            <LiveChip testid="warranty-forecast-live" />
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
          {t("app.warranty_forecast", "Warranty forecast")}
        </div>
        <div className="text-sm text-white font-semibold leading-snug">
          {willCross
            ? t("app.warranty_will_cross", "Projected to cross {{pct}}% at year {{yr}}", {
                pct: vehicle.warranty?.threshold || 70,
                yr: forecast.yearsToWarranty,
              })
            : t("app.warranty_stays", "Stays above {{pct}}% through year 8", {
                pct: vehicle.warranty?.threshold || 70,
              })}
        </div>
        <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
          {t("app.pace", "At {{p}}%/mo current pace", {
            p: Math.abs(vehicle.delta30d).toFixed(2),
          })}{" "}
          · {climate.label}
          {vehicle.sohSuspect && (
            <span className="text-amber-400/80" data-testid="soh-suspect-note">
              {" "}· range-based reading looked inconsistent with this pack's age — showing an age-based estimate while we verify the trim baseline
            </span>
          )}
        </div>
      </div>

      <PredictorSlot
        currentSoh={vehicle.sohCurrent}
        monthlyLoss={Math.abs(vehicle.delta30d || 0.2)}
        climateZone={climate.zone}
        vehicleAgeYears={vehicle.ageYears || Math.max(0.5, new Date().getFullYear() - (vehicle.year || new Date().getFullYear() - 3))}
        milesPerYear={hints.miles_per_year || 12000}
        warrantyThreshold={warrantyFor(vehicle).threshold}
        warrantyYears={warrantyFor(vehicle).years}
        warrantyMiles={warrantyFor(vehicle).miles}
        dataMonths={vehicle.dataMonths || (vehicle.velocitySource === "measured" ? 12 : 3)}
        brandName={vehicle.brand === "ford" ? "Ford" : vehicle.brand === "volvo" ? "Volvo" : "Tesla"}
      />
      </CollapseSection>

      <CollapseSection id="home-energy" eyebrow="Energy" title="Charging & drain">
      <ChargingTodayCard vehicle={vehicle} climate={climate} />

      <FordWallboxCard vehicle={vehicle} />

      <PhantomDrainCard vehicle={vehicle} climate={climate} />

      <EarthImpactCard vin={vehicle.vin} />
      </CollapseSection>

      <CollapseSection id="home-garage" eyebrow="Garage" title="Modifications">
      <ModificationsCard vehicle={vehicle} />
      </CollapseSection>
        </>
      )}

      <CollapseSection id="home-community" eyebrow="Community" title="Share EVIQ">
      <ShareAppCard />

      <GoogleReviewCard />
      </CollapseSection>
    </div>
  );
};
